/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

import { useContext } from 'react'
import {
  FormattedDate,
  FormattedMessage,
  FormattedRelativeTime,
  FormattedTime,
  useIntl
} from 'react-intl'
import ButtonGroup from '../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../client/js/components/form/SmartForm'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../client/js/lib/form'
import { AuthContext } from '../../auth/components/AuthProvider'
import { patchUser, postUser } from '../lib/userAPIClient'
import UserSchema from '../schemas/UserSchema.mjs'
import EmailField from './fields/EmailField'
import EnabledField from './fields/EnabledField'
import RoleField from './fields/RoleField'
import UsernameField from './fields/UsernameField'
import { getUserListLink } from '../lib/userLinks'
import FormResetButton from '../../../client/js/components/form/buttons/FormResetButton'
import { Alert, Box, PasswordInput, Stack, Text } from '@mantine/core'
import FormSection from '../../twh/components/FormSection'
import useCustomForm from '../../../client/js/hooks/useCustomForm'
import FormField from '../../../client/js/components/form/fields/FormField'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle'
import { DateTime } from 'luxon'

export type UserFormProps = {
  initialValues: Partial<User>
}

function UserForm (props: UserFormProps) {
  const { initialValues } = props
  const intl = useIntl()
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:user')
  const isCurrentUser = initialValues && auth.context.username === initialValues.username

  const form = useCustomForm({
    disabled: !editable,
    initialValues,
    initializeField: createFieldInitializer(UserSchema),
    validate: createFormValidator(UserSchema),
    validateField: createFieldValidator(UserSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, UserSchema)

      // Remove fields that current user cannot change.
      if (isCurrentUser) {
        delete finalValues.enabled
        delete finalValues.roleId
      }
      return id ? patchUser(id, finalValues) : postUser(finalValues)
    }
  })

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getUserListLink() : undefined}
    >
      <Stack>
        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'general',
            defaultMessage: 'Général'
          })}
        >
          <UsernameField autoComplete="off" />
          <Box>
            <FormField
              name="password"
              autoComplete="new-password"
              label={intl.formatMessage({
                id: 'password',
                defaultMessage: 'Mot de passe'
              })}
              render={PasswordInput}
            />
            <Text size="xs" c="gray">
              {initialValues.passwordDate
                ? <FormattedMessage
                  id="password_changed_at"
                  defaultMessage="Le mot de passe a été défini {since}, le {date} à {time}"
                  values={{
                    date: <FormattedDate value={initialValues.passwordDate} />,
                    time: <FormattedTime value={initialValues.passwordDate} />,
                    since: (
                      <FormattedRelativeTime
                        numeric="auto"
                        value={(new Date(initialValues.passwordDate).getTime() -
                          Date.now()) / 1000}
                        updateIntervalInSeconds={1}
                      />
                    )
                  }}
                />
                : null}
            </Text>
          </Box>
          <EmailField />

          {form.values.id != null
            ? form.values.connectedAt != null
              ? <Alert p="xs" icon={<FontAwesomeIcon icon={faInfoCircle} />}>
                <FormattedMessage
                  id="last_authentication_%s"
                  values={{
                    reltime: <FormattedRelativeTime
                      numeric="auto"
                      value={(DateTime.fromISO(form.values.connectedAt).toMillis() - Date.now()) / 1000}
                      updateIntervalInSeconds={1}
                    />,
                    date: intl.formatDate(form.values.connectedAt, {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })
                  }}
                />
              </Alert>
              : <Alert p="xs" icon={<FontAwesomeIcon icon={faInfoCircle} />}>
                <FormattedMessage id="user_not_yet_authenticated" />
              </Alert>
            : null}
        </FormSection>

        <FormSection
          defaultOpened
          title={intl.formatMessage({
            id: 'security',
            defaultMessage: 'Sécurité'
          })}
        >
          <RoleField disabled={isCurrentUser} />
          <EnabledField disabled={isCurrentUser} />
        </FormSection>

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default UserForm
